import { calculateHeaderGroupHeight } from '@theme/critical';
import { Component } from '@theme/component';
import { onDocumentLoaded, changeMetaThemeColor } from '@theme/utilities';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */
class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /**
   * Width of window when header drawer was hidden.
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * Current menu hidden state, used to avoid repeated class writes.
   * @type {boolean | null}
   */
  #menuHidden = null;

  /**
   * Last measured header height.
   * @type {number}
   */
  #lastHeaderHeight = 0;

  /**
   * An intersection observer for monitoring sticky header position.
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'.
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up'.
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * Scroll animation frame id.
   * @type {number | null}
   */
  #scrollRaf = null;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'.
   * @type {number | null}
   */
  #timeout = null;

  /**
   * The duration to wait for hiding animation, when sticky behavior is 'scroll-up'.
   * @constant {number}
   */
  #animationDelay = 150;

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume.
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry) return;

    const height = this.#getResizeEntryHeight(entry);

    if (height && Math.abs(height - this.#lastHeaderHeight) > 0.5) {
      this.#lastHeaderHeight = height;
      document.body.style.setProperty('--header-height', `${Math.round(height)}px`);
    }

    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  /**
   * Reads ResizeObserver height without forcing layout when possible.
   * Falls back to getBoundingClientRect only if needed.
   *
   * @param {ResizeObserverEntry} entry
   * @returns {number}
   */
  #getResizeEntryHeight(entry) {
    const borderBoxSize = entry.borderBoxSize;
    const boxSize = Array.isArray(borderBoxSize) ? borderBoxSize[0] : borderBoxSize;

    if (boxSize?.blockSize) {
      return boxSize.blockSize;
    }

    if (entry.contentRect?.height) {
      return entry.contentRect.height;
    }

    return entry.target.getBoundingClientRect().height;
  }

  /**
   * Sets a dataset value only when it actually changed.
   *
   * @param {string} key
   * @param {string} value
   */
  #setDataValue(key, value) {
    if (this.dataset[key] !== value) {
      this.dataset[key] = value;
    }
  }

  /**
   * Toggles the data-animating attribute without repeated DOM writes.
   *
   * @param {boolean} isAnimating
   */
  #setAnimating(isAnimating) {
    if (isAnimating) {
      if (!this.hasAttribute('data-animating')) {
        this.setAttribute('data-animating', '');
      }
    } else if (this.hasAttribute('data-animating')) {
      this.removeAttribute('data-animating');
    }
  }

  /**
   * Clears the sticky animation timeout.
   */
  #clearStickyTimeout() {
    if (!this.#timeout) return;

    window.clearTimeout(this.#timeout);
    this.#timeout = null;
  }

  /**
   * Observes the header while scrolling the viewport to track when it's actively sticky.
   *
   * @param {boolean} alwaysSticky - Determines if we need to observe when the header is offscreen.
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    this.#intersectionObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;

        const { isIntersecting } = entry;

        if (alwaysSticky) {
          const nextStickyState = isIntersecting ? 'inactive' : 'active';

          if (this.dataset.stickyState !== nextStickyState) {
            this.dataset.stickyState = nextStickyState;
            changeMetaThemeColor(this.refs.headerRowTop);
          }
        } else {
          this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
        }
      },
      {
        threshold: alwaysSticky ? 1 : 0,
      }
    );

    this.#intersectionObserver.observe(this);
  };

  /**
   * Handles the overflow minimum event from the header menu.
   *
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer.
   *
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer.
   */
  #updateMenuVisibility(hideMenu) {
    if (this.#menuHidden === hideMenu) return;

    this.#menuHidden = hideMenu;

    if (hideMenu) {
      this.refs.headerDrawerContainer.classList.remove('desktop:hidden');
      this.refs.headerMenu.classList.add('hidden');
      this.#menuDrawerHiddenWidth = window.innerWidth;
      return;
    }

    this.refs.headerDrawerContainer.classList.add('desktop:hidden');
    this.refs.headerMenu.classList.remove('hidden');
    this.#menuDrawerHiddenWidth = null;
  }

  /**
   * Schedules sticky scroll work in requestAnimationFrame.
   * This avoids doing layout/style work directly inside the scroll event.
   */
  #handleWindowScroll = () => {
    if (this.#scrollRaf !== null) return;

    this.#scrollRaf = window.requestAnimationFrame(this.#updateStickyOnScroll);
  };

  /**
   * Updates sticky state after scroll, throttled by requestAnimationFrame.
   */
  #updateStickyOnScroll = () => {
    this.#scrollRaf = null;

    const stickyMode = this.getAttribute('sticky');

    if (!this.#offscreen && stickyMode !== 'always') return;

    const scrollTop = document.scrollingElement?.scrollTop ?? window.scrollY ?? 0;
    const isScrollingUp = scrollTop < this.#lastScrollTop;
    const isAtTop = scrollTop <= 1;

    this.#clearStickyTimeout();

    if (stickyMode === 'always') {
      if (isAtTop) {
        this.#setDataValue('scrollDirection', 'none');
      } else if (isScrollingUp) {
        this.#setDataValue('scrollDirection', 'up');
      } else {
        this.#setDataValue('scrollDirection', 'down');
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    if (isScrollingUp) {
      this.#setAnimating(false);

      if (isAtTop) {
        this.#offscreen = false;
        this.#setDataValue('stickyState', 'inactive');
        this.#setDataValue('scrollDirection', 'none');
      } else {
        this.#setDataValue('stickyState', 'active');
        this.#setDataValue('scrollDirection', 'up');
      }
    } else if (this.dataset.stickyState === 'active') {
      this.#setDataValue('scrollDirection', 'none');
      this.#setAnimating(true);

      this.#timeout = window.setTimeout(() => {
        this.#setDataValue('stickyState', 'idle');
        this.#setAnimating(false);
        this.#timeout = null;
      }, this.#animationDelay);
    } else {
      this.#setDataValue('scrollDirection', 'none');
      this.#setDataValue('stickyState', 'idle');
    }

    this.#lastScrollTop = scrollTop;
  };

  connectedCallback() {
    super.connectedCallback();

    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);

    const stickyMode = this.getAttribute('sticky');

    if (stickyMode) {
      this.#observeStickyPosition(stickyMode === 'always');

      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        document.addEventListener('scroll', this.#handleWindowScroll, { passive: true });
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.#intersectionObserver = null;

    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    document.removeEventListener('scroll', this.#handleWindowScroll);

    this.#clearStickyTimeout();

    if (this.#scrollRaf !== null) {
      window.cancelAnimationFrame(this.#scrollRaf);
      this.#scrollRaf = null;
    }

    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector('#header-component');
  const headerGroup = document.querySelector('#header-group');

  if (!headerGroup) return;

  let frame = null;
  let lastHeaderGroupHeight = -1;

  const observedElements = new WeakSet();

  const setBodyStyleProperty = (name, value) => {
    if (document.body.style.getPropertyValue(name) !== value) {
      document.body.style.setProperty(name, value);
    }
  };

  const calculateAndApplyHeaderGroupHeight = () => {
    frame = null;

    const height = Math.round(calculateHeaderGroupHeight(header, headerGroup));

    if (height === lastHeaderGroupHeight) return;

    lastHeaderGroupHeight = height;
    setBodyStyleProperty('--header-group-height', `${height}px`);
  };

  const scheduleHeaderGroupHeightCalculation = () => {
    if (frame !== null) return;

    frame = window.requestAnimationFrame(calculateAndApplyHeaderGroupHeight);
  };

  const resizeObserver = new ResizeObserver(scheduleHeaderGroupHeightCalculation);

  const observeElement = (element) => {
    if (!(element instanceof HTMLElement)) return;
    if (element === header) return;
    if (observedElements.has(element)) return;

    observedElements.add(element);
    resizeObserver.observe(element);
  };

  for (let i = 0; i < headerGroup.children.length; i += 1) {
    observeElement(headerGroup.children[i]);
  }

  const mutationObserver = new MutationObserver((mutations) => {
    let shouldRecalculate = false;

    for (const mutation of mutations) {
      if (mutation.type !== 'childList') continue;

      shouldRecalculate = true;

      for (const node of mutation.addedNodes) {
        observeElement(node);
      }

      for (const node of mutation.removedNodes) {
        if (node instanceof HTMLElement) {
          resizeObserver.unobserve(node);
        }
      }
    }

    if (shouldRecalculate) {
      scheduleHeaderGroupHeightCalculation();
    }
  });

  mutationObserver.observe(headerGroup, { childList: true });

  scheduleHeaderGroupHeightCalculation();
});